/**
 * 国土地理院「地理院タイル」の写真系レイヤー定義
 *
 * 出典: 地理院タイル一覧 https://maps.gsi.go.jp/development/ichiran.html
 *
 * ここで定義した配列を `overlayGroups.tsx`（レイヤーコントロールの項目）と
 * `LeafletMap.tsx`（実際のタイル描画）の双方が参照し、定義の二重管理を避ける。
 */

const GSI_TILE_HOST = "https://cyberjapandata.gsi.go.jp/xyz";

export type GsiTileExtension = "jpg" | "png";

/** 地理院タイルの URL テンプレートを組み立てる。 */
export const buildGsiTileUrl = (
  tileId: string,
  ext: GsiTileExtension
): string => `${GSI_TILE_HOST}/${tileId}/{z}/{x}/{y}.${ext}`;

export const GSI_ATTRIBUTION_URL =
  "https://maps.gsi.go.jp/development/ichiran.html";

/** Leaflet の出典欄に表示する国土地理院リンク。 */
export const GSI_PHOTO_ATTRIBUTION = `<a href="${GSI_ATTRIBUTION_URL}">国土地理院</a>`;

/**
 * GRUS 画像を含むタイル（全国最新写真・年度別空中写真）用の出典。
 * Leaflet の出典欄は同一文字列を 1 回だけ表示するため、該当レイヤーは必ずこの定数を共有する。
 */
export const GSI_PHOTO_ATTRIBUTION_WITH_GRUS = `${GSI_PHOTO_ATTRIBUTION}, GRUS画像（© Axelspace）`;

// ---------------------------------------------------------------------------
// 年度別空中写真（2007年以降）
// ---------------------------------------------------------------------------

/** レイヤーコントロールのキー。年度は `${NENDO_PHOTO_LAYER_NAME}_${year}` で管理する。 */
export const NENDO_PHOTO_LAYER_NAME = "年度別空中写真（2007年以降）";

/** 地理院が配信する最小ズームレベル。 */
export const NENDO_PHOTO_MIN_ZOOM = 14;
/** 地理院が配信する最大ズームレベル。これを超える範囲は拡大表示で補う。 */
export const NENDO_PHOTO_MAX_NATIVE_ZOOM = 18;

const NENDO_PHOTO_FIRST_YEAR = 2007;
/** 地理院地図のレイヤー定義（layers_txt）で配信が確認できた最新年度。 */
const NENDO_PHOTO_LATEST_YEAR = 2026;

/** 選択可能な年度（新しい年度が先頭）。 */
export const NENDO_PHOTO_YEARS: readonly number[] = Array.from(
  { length: NENDO_PHOTO_LATEST_YEAR - NENDO_PHOTO_FIRST_YEAR + 1 },
  (_, index) => NENDO_PHOTO_LATEST_YEAR - index
);

export const getNendoPhotoTileUrl = (year: number): string =>
  buildGsiTileUrl(`nendophoto${year}`, "png");

/** 年度別空中写真の年度キー（`${NENDO_PHOTO_LAYER_NAME}_2024` 形式）かどうか。 */
export const isNendoPhotoYearKey = (layerName: string): boolean =>
  layerName.startsWith(`${NENDO_PHOTO_LAYER_NAME}_`);

/**
 * レイヤー名の一覧から、年度別空中写真の年度キーだけを「表示中」として取り出す。
 *
 * LeafletMap の overlayVisibility は初期化時に一度だけ checkedLayers から組み立てるため、
 * 年度キーをここで拾わないと、再マウント時に選択中の年度が落ちて
 * 「親レイヤーは ON なのにタイルもチェックも出ない」状態になる。
 */
export const pickNendoPhotoYearVisibility = (
  layerNames: readonly string[]
): Record<string, boolean> =>
  Object.fromEntries(
    layerNames.filter(isNendoPhotoYearKey).map((name) => [name, true])
  );

// ---------------------------------------------------------------------------
// 年代別の写真（1928年頃〜1990年）
// ---------------------------------------------------------------------------

export interface GsiHistoricalPhotoLayer {
  /** レイヤーコントロールのキー（overlayVisibility / checkedLayers で使用） */
  name: string;
  /** UI 上の表示ラベル */
  label: string;
  /** 地理院タイルのタイルID（URL のパス部分） */
  tileId: string;
  ext: GsiTileExtension;
  /** 地理院が配信する最小ズームレベル */
  minZoom: number;
  /** 地理院が配信する最大ズームレベル（これを超える範囲は拡大表示で補う） */
  maxNativeZoom: number;
  /** ヘルプに表示する説明文 */
  description: string;
}

const HISTORICAL_DESCRIPTION_PREFIX = "国土地理院が提供する";

const historicalLayer = (
  label: string,
  tileId: string,
  ext: GsiTileExtension,
  minZoom: number,
  maxNativeZoom: number,
  coverageNote?: string
): GsiHistoricalPhotoLayer => ({
  name: `空中写真（${label}）`,
  label,
  tileId,
  ext,
  minZoom,
  maxNativeZoom,
  description: coverageNote
    ? `${HISTORICAL_DESCRIPTION_PREFIX}${label}に撮影された空中写真を表示します。提供範囲は${coverageNote}です。`
    : `${HISTORICAL_DESCRIPTION_PREFIX}${label}に撮影された空中写真を表示します。撮影範囲は年代により異なります。`,
});

/** 年代別の写真（新しい年代が先頭）。 */
export const GSI_HISTORICAL_PHOTO_LAYERS: readonly GsiHistoricalPhotoLayer[] = [
  historicalLayer("1987年～1990年", "gazo4", "jpg", 10, 17),
  historicalLayer("1984年～1986年", "gazo3", "jpg", 10, 17),
  historicalLayer("1979年～1983年", "gazo2", "jpg", 10, 17),
  historicalLayer("1974年～1978年", "gazo1", "jpg", 10, 17),
  historicalLayer("1961年～1969年", "ort_old10", "png", 10, 17),
  historicalLayer("1945年～1950年", "ort_USA10", "png", 10, 17),
  historicalLayer(
    "1936年～1942年頃",
    "ort_riku10",
    "png",
    13,
    18,
    "東京23区内（世田谷区の一部を除く）と大阪市およびその周辺"
  ),
  historicalLayer("1928年頃", "ort_1928", "png", 13, 18, "大阪市域"),
];

/** レイヤーコントロールで年代別の写真をまとめる折りたたみ見出し。 */
export const DATED_PHOTO_FOLDER_TITLE = "年代別の写真";
